// ============================================================================
// Hooks TanStack Query — ADMINISTRATION (AD-B) : onglets Overview + Teams
// ============================================================================
// Hook = TanStack Query SEULEMENT (regle parc). L'autorisation vit au SERVEUR
// (P-ADM-2, routes/admin.ts : canAccessGestAdmin = admin SEUL) ; le SPA consomme
// les bundles par onglet (P-ADM-8) sous la racine ADMIN_ROOT_KEY (useAdminAudit).
// Les mutations invalident la FAMILLE admin entiere (['gest','admin']) + la liste
// /equipes (['gest','teams']) : nom/departement/responsable y sont affiches
// (parite PoC revalidatePath('/equipes')).

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiForbiddenError, ApiServerError } from '@gs/api-client';
import { ADMIN_ROOT_KEY, type GestAdminRole } from './useAdminAudit';

// ─── Types — MIROIR EXACT des DTOs serveur (routes/admin.ts AD-B) ─────────────

/** Ligne equipe du bundle admin (miroir TeamSummary serveur — sans badgePulses). */
export interface AdminTeamSummary {
  id: string;
  name: string;
  department: string | null;
  headcount: number;
  directorEmail: string | null;
  directorName: string | null;
  memberCount: number;
  latestMood: number | null;
  latestPeriod: string | null;
  currentMood: number | null;
  submittedThisPeriod: boolean;
  objectivesTotal: number;
  objectivesDone: number;
  objectivesInProgress: number;
}

/** Personne assignable comme responsable (ce/cg + directors actuels — STOP-8). */
export interface AdminAssignablePerson {
  email: string;
  fullName: string | null;
}

export interface AdminOverviewResponse {
  viewer: { role: GestAdminRole; email: string };
  kpis: {
    members: {
      total: number;
      admins: number;
      managers: number;
      directors: number;
      employees: number;
      committees: { total: number; ce: number; cg: number };
    };
    teams: {
      total: number;
      withoutDirector: number;
      submittedCount: number;
      moodAvg: number | null;
    };
    objectives: { total: number; done: number; inProgress: number };
  };
}

export interface AdminTeamsResponse {
  viewer: { role: GestAdminRole; email: string };
  teams: AdminTeamSummary[];
  assignable: AdminAssignablePerson[];
}

export interface TeamCreatePayload {
  name: string;
  department: string | null;
  headcount: number;
}

export interface TeamUpdatePayload extends TeamCreatePayload {
  /** null = aucun responsable (STOP-8 : affectation decouplee du role UCBR). */
  directorEmail: string | null;
}

// ─── Queries (bundles par onglet, P-ADM-8) ────────────────────────────────────

const adminOverviewKey = [...ADMIN_ROOT_KEY, 'overview'] as const;
const adminTeamsKey = [...ADMIN_ROOT_KEY, 'teams'] as const;

/** Code court d'erreur mutation -> message i18n (lecon GA-21, parite teamErrorCode). */
export function adminTeamErrorCode(err: unknown): string {
  if (err instanceof ApiForbiddenError) return 'forbidden';
  if (err instanceof ApiServerError) {
    if (err.status === 404) return 'notfound';
    if (err.status === 400) return 'invalid';
  }
  return 'server';
}

/** AD-B : bundle de l'onglet VUE D'ENSEMBLE (1 requete SPA — zero KPI mail, P-ADM-7). */
export function useAdminOverview(enabled: boolean) {
  return useQuery({
    queryKey: adminOverviewKey,
    queryFn: () => api.get<AdminOverviewResponse>('/api/admin/overview'),
    enabled,
    retry: false,
  });
}

/** AD-B : bundle de l'onglet EQUIPES (liste + pool assignable, 1 requete SPA). */
export function useAdminTeams(enabled: boolean) {
  return useQuery({
    queryKey: adminTeamsKey,
    queryFn: () => api.get<AdminTeamsResponse>('/api/admin/teams'),
    enabled,
    retry: false,
  });
}

// ─── Mutations (les 2 premieres ecritures admin — POST/PUT /teams) ────────────

function useInvalidateAdminTeams() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ADMIN_ROOT_KEY });
    // La liste /equipes affiche nom/departement/responsable (parite PoC
    // revalidatePath('/equipes')) — on invalide aussi cette famille.
    void qc.invalidateQueries({ queryKey: ['gest', 'teams'] });
    void qc.invalidateQueries({ queryKey: ['gest', 'grille-ce'] });
    void qc.invalidateQueries({ queryKey: ['gest', 'org'] });
  };
}

/** AD-B : creation d'une equipe (director NULL a la creation, parite PoC). */
export function useCreateTeam() {
  const invalidate = useInvalidateAdminTeams();
  return useMutation({
    mutationFn: (payload: TeamCreatePayload) => api.post<{ id: string }>('/api/admin/teams', payload),
    onSuccess: () => invalidate(),
  });
}

/** AD-B : maj nom/departement/effectif/responsable (audit team.updated serveur). */
export function useUpdateTeam() {
  const invalidate = useInvalidateAdminTeams();
  return useMutation({
    mutationFn: (input: { id: string; payload: TeamUpdatePayload }) =>
      api.put<{ ok: boolean }>(`/api/admin/teams/${input.id}`, input.payload),
    onSuccess: () => invalidate(),
  });
}
